import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBookingFlowStore, CustomerInfo } from '../../stores/useBookingFlowStore';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { bookingApi, linePublicApi } from '../../services/api';
import ProgressBar from '../../components/booking/ProgressBar';
import { Input } from '@redpanda/ui';
import FormFieldWithError from '../../components/forms/FormFieldWithError';
import { toast } from 'react-hot-toast';

interface FormData {
  name: string;
  email: string;
  phone: string;
  note?: string;
}

const InfoPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const {
    service,
    dateTime,
    staff,
    customerInfo,
    setCustomerInfo,
    setBookingResult,
    canProceedToInfo,
  } = useBookingFlowStore();
  const { customer, isAuthenticated } = useCustomerAuth();
  const [searchParams] = useSearchParams();

  const [submitting, setSubmitting] = useState(false);
  const [lineLoginUrl, setLineLoginUrl] = useState<string | null>(null);
  const [lineLoginEnabled, setLineLoginEnabled] = useState(false);
  const [lineUserData, setLineUserData] = useState<{ userId: string; displayName: string; pictureUrl: string | null } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      name: customerInfo?.name || '',
      email: customerInfo?.email || '',
      phone: customerInfo?.phone || '',
      note: customerInfo?.note || '',
    },
  });

  // 解析 URL 中的 lineData（LINE OAuth 回調後）
  useEffect(() => {
    const lineDataParam = searchParams.get('lineData');
    if (lineDataParam) {
      try {
        const decoded = JSON.parse(atob(lineDataParam));
        setLineUserData(decoded);
        setValue('name', decoded.displayName || '');
      } catch (e) {
        console.error('Failed to parse lineData:', e);
      }
    }
  }, [searchParams, setValue]);

  // 取得 LINE Login URL
  useEffect(() => {
    if (!storeId) return;
    linePublicApi.getLoginUrl(storeId).then((res) => {
      if (res.data.enabled) {
        setLineLoginEnabled(true);
        setLineLoginUrl(res.data.loginUrl);
      }
    }).catch(() => {});
  }, [storeId]);

  // 當客戶已登入時自動填入資料
  useEffect(() => {
    if (isAuthenticated && customer) {
      setValue('name', customer.name);
      setValue('email', customer.email);
      if (customer.phone) {
        setValue('phone', customer.phone);
      }
    }
  }, [isAuthenticated, customer, setValue]);

  // 驗證是否可以進入此頁面
  useEffect(() => {
    if (!canProceedToInfo()) {
      navigate(`/book/${storeId}`);
    }
  }, [canProceedToInfo, navigate, storeId]);

  const onSubmit = async (data: FormData) => {
    if (!service || !dateTime) {
      toast.error(t('pages:booking.info.bookingError'));
      return;
    }

    try {
      setSubmitting(true);

      // 保存客戶資訊到 store
      const customerData: CustomerInfo = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        note: data.note,
        customerLineId: lineUserData?.userId,
      };
      setCustomerInfo(customerData);

      // 提交預約
      const bookingData = {
        customerName: data.name,
        customerEmail: data.email,
        customerPhone: data.phone,
        customerLineId: lineUserData?.userId,
        storeId: storeId!,
        serviceId: service.id,
        staffId: staff?.id,
        date: new Date(dateTime.time),
        note: data.note,
      };

      const response = await bookingApi.create(bookingData);
      const booking = response.data;

      // 保存預約結果
      setBookingResult(booking.id, booking.managementToken);

      // 導航到確認頁面
      navigate(`/book/${storeId}/confirmation`);
    } catch (error: any) {
      console.error('Failed to create booking:', error);
      toast.error(error.response?.data?.message || t('pages:booking.info.bookingFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!service || !dateTime) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* 進度條 */}
        <ProgressBar
          currentStep={3}
          totalSteps={4}
          steps={[
            t('pages:booking.storeHome.selectService'),
            t('pages:booking.dateTime.title'),
            t('pages:booking.info.title'),
            t('pages:booking.confirmation.successTitle')
          ]}
        />

        {/* 返回按鈕 */}
        <button
          onClick={() => navigate(`/book/${storeId}/staff`)}
          className="mb-6 flex items-center space-x-2 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
          disabled={submitting}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('pages:booking.info.backToStaff')}</span>
        </button>

        {/* 頁面標題 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            {t('pages:booking.info.title')}
          </h1>

          {/* 預約摘要 */}
          <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4 space-y-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('pages:booking.info.bookingSummary')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.info.serviceItem')}</span>
                <span className="font-semibold text-gray-900 dark:text-white ml-2">
                  {service.name}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.info.bookingTime')}</span>
                <span className="font-semibold text-gray-900 dark:text-white ml-2">
                  {dateTime.date} {dateTime.displayTime}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.info.serviceDuration')}</span>
                <span className="font-semibold text-gray-900 dark:text-white ml-2">
                  {service.duration} {t('translation:common.time.minutes')}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.info.servicePrice')}</span>
                <span className="font-semibold text-gray-900 dark:text-white ml-2">
                  ${Math.floor(service.price)}
                </span>
              </div>
              {staff && (
                <div className="md:col-span-2">
                  <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.info.staffMember')}</span>
                  <span className="font-semibold text-gray-900 dark:text-white ml-2">
                    {staff.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 客戶資訊表單 */}
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            {t('pages:booking.info.contactInfo')}
          </h2>

          {/* LINE Login 按鈕 */}
          {lineLoginEnabled && !lineUserData && lineLoginUrl && (
            <div className="mb-6">
              <a
                href={lineLoginUrl}
                className="w-full flex items-center justify-center gap-2 text-white font-bold py-3 rounded-lg text-base transition-all duration-200 hover:opacity-90"
                style={{ backgroundColor: '#06C755' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                </svg>
                {t('pages:booking.info.lineLoginButton')}
              </a>
              <p className="text-xs text-gray-400 text-center mt-2">{t('pages:booking.info.lineLoginHint')}</p>
            </div>
          )}

          {/* LINE 已綁定 */}
          {lineUserData && (
            <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3 flex items-center gap-3">
              {lineUserData.pictureUrl && (
                <img src={lineUserData.pictureUrl} alt="" className="w-10 h-10 rounded-full" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">{lineUserData.displayName}</p>
                <p className="text-xs text-green-600 dark:text-green-400">{t('pages:booking.info.lineLinked')}</p>
              </div>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#06C755">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </div>
          )}

          <div className="space-y-6">
            {/* 姓名 */}
            <FormFieldWithError
              label={t('pages:booking.info.name')}
              required
              error={errors.name?.message}
              htmlFor="name"
            >
              <Input
                id="name"
                type="text"
                {...register('name', {
                  required: t('pages:booking.info.nameRequired'),
                  minLength: { value: 2, message: t('pages:booking.info.nameMinLength') },
                })}
                variant={errors.name ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* Email */}
            <FormFieldWithError
              label={t('pages:booking.info.email')}
              required
              error={errors.email?.message}
              htmlFor="email"
            >
              <Input
                id="email"
                type="email"
                {...register('email', {
                  required: t('pages:booking.info.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('pages:booking.info.emailInvalid'),
                  },
                })}
                variant={errors.email ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 電話 */}
            <FormFieldWithError
              label={t('pages:booking.info.phone')}
              required={true}
              error={errors.phone?.message}
              htmlFor="phone"
            >
              <Input
                id="phone"
                type="tel"
                {...register('phone', {
                  required: t('pages:booking.info.phoneRequired'),
                  pattern: {
                    value: /^[0-9\-\s\+\(\)]+$/,
                    message: t('pages:booking.info.phoneInvalid'),
                  },
                })}
                variant={errors.phone ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 備註（選填） */}
            <FormFieldWithError
              label={t('pages:booking.info.note')}
              required={false}
              error={errors.note?.message}
              htmlFor="note"
            >
              <textarea
                id="note"
                rows={4}
                {...register('note')}
                disabled={submitting}
                className="w-full px-4 py-3 border-2 border-slate-300 dark:border-slate-600 rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:border-brand-500 dark:bg-slate-700 dark:text-white transition-colors resize-none"
              />
            </FormFieldWithError>
          </div>

          {/* 提交按鈕 */}
          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className={`
                px-8 py-3 rounded-lg font-bold text-white transition-all duration-200
                ${
                  submitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700 shadow-lg hover:shadow-xl'
                }
              `}
            >
              {submitting ? (
                <span className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t('pages:booking.info.booking')}</span>
                </span>
              ) : (
                t('pages:booking.info.confirmBooking')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InfoPage;
